import { create } from "zustand";
import axiosInstance from "../api/axiosInstance";
import { storage } from "../utils/storage";


interface AuthState {
  email: string;
  password: string;
  newPassword: string;
  confirmPassword: string;
  error: string | null;
  isLoading: boolean;
  successMessage: string | null;
  emailExist:boolean,
  emailExistMessage: string | null;

  setEmail: (email: string) => void;
  checkEmail: (email: string) => void;
  setPassword: (password: string) => void;
  setNewPassword: (newPassword: string) => void;
  setConfirmPassword: (confirmPassword: string) => void;

  login: () => Promise<any>;
  logout: () => void;
  forgotPassword: () => Promise<void>;
  resetPassword: (token: string, newPassword: string, confirmPassword: string) => Promise<void>;
  setSuccessMessage: (message: string) => void;
  setEmailExistMessage: (message: string) => void;
  setError: (message: string) => void;
  token: string | null;
  user: any | null;
  isAuthenticated: boolean;
}

const useAuthStore = create<AuthState>((set, get) => ({
  email: "",
  password: "",
  newPassword: "",
  confirmPassword: "",
  error: null,
  successMessage: null,
  emailExistMessage:null,
  isLoading: false,
  emailExist:false,

  setEmail: (email) => set({ email }),
  setPassword: (password) => set({ password }),
  setNewPassword: (newPassword) => set({ newPassword }),
  setConfirmPassword: (confirmPassword) => set({ confirmPassword }),
  setSuccessMessage: (message) => set({ successMessage: message }),
  setEmailExistMessage: (message) => set({ emailExistMessage: message }),
  setError: (msg: string) => set({ error: msg }),

    // ✅ Safe read from storage
  token: storage?.getItem("token") || null,
  user: storage ? JSON.parse(storage.getItem("user") || "null") : null,
  isAuthenticated: !!(storage?.getItem("token")),

   checkEmail: async (email) => {
    
     if (!email) {
      set({ error: "Email is required!", isLoading: false });
      return;
    }

    try {
      const response = await axiosInstance.post("/check-email", { email });

      if (response.data.exists) {
        
        useAuthStore.getState().setEmailExistMessage("Email already exists. Try logging in or use a different one.");
        
      } else {
        useAuthStore.getState().setEmailExistMessage("")
      }
    } catch (error: any) {
      useAuthStore.getState().setError(error.response?.data?.error || "Could not verify email. Please try again." );
    } finally {
      set({ isLoading: false });
    }

   },
  // ✅ Login function
  login: async () => {
    const { email, password } = get();
    set({ isLoading: true, error: null });

    if (!email || !password) {
      set({ isLoading: false });
      useAuthStore.getState().setError("Email and Password are required!");
      return;
    }

    try {
      const response = await axiosInstance.post("/Userlogin", { email, password });

      if(response.status === 200 && response.data.error===''){
        console.log("Login successful:", response.data.authUserState);

        set({
          token: response.data.token,
          user: response.data.authUserState,
          isAuthenticated: true,
        });
         
        // ✅ Safe write to storage
        if (storage) {
          storage.setItem("token", response.data.token);
          storage.setItem("user", JSON.stringify(response.data.authUserState));
        }
        return response; 
       
      } else {
       
         useAuthStore.getState().setError(response.data.error || "Invalid credentials");
      }
    } catch (error: any) {
     
      useAuthStore.getState().setError(error.response?.data?.error || "Login failed");
    } finally {
      set({ isLoading: false });
    }
  },

  // ✅ Forgot Password function
  forgotPassword: async () => {
    const { email } = get();
    set({ isLoading: true, error: null, successMessage: null });

    if (!email) {
      set({isLoading: false });
      useAuthStore.getState().setError("Email is required!");
      return;
    }

    try {
      const response = await axiosInstance.post("/forgotPassword", { email });

      if (response.data.msg) {
        set({ successMessage: "Password reset link sent to your email!" });
      } else {
        useAuthStore.getState().setError(response.data.error || "Something went wrong!");
      }
    } catch (error: any) {
     
      useAuthStore.getState().setError(error.response?.data?.msg || "Failed to send reset link");
      
    } finally {
      set({ isLoading: false });
    }
  },

  resetPassword: async (token: string, newPassword: string, confirmPassword: string) => {
    set({ isLoading: true, error: null, successMessage: null });
  
    if (!newPassword || !confirmPassword) {
      set({ error: "All fields are required!", isLoading: false });
      return;
    }
  
    if (newPassword !== confirmPassword) {
      set({ error: "Passwords do not match!", isLoading: false });
      return;
    }
  
    try {
      const response = await axiosInstance.post("/resetPassword", {
        token,
        newPassword,
      });
  
      if (response.data.success) {
        set({ successMessage: "Password reset successfully!" });
      } else {
        set({ error: response.data.error || "Something went wrong!" });
      }
    } catch (error: any) {
      set({ error: error.response?.data?.error || "Failed to reset password" });
    } finally {
      set({ isLoading: false });
    }
  },

  logout: () => {
    set({ token: null, user: null, isAuthenticated: false });
     if (storage) {
          storage.removeItem("token");
          storage.removeItem("user");
        }
   
  },
  
}));

export default useAuthStore;
